import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ItemInterceptionModal } from '@/types/modal';
import { useItem, useUpdateItemInterception } from '@/hooks/item';
import { ErrorModal, StandardModal, NoFooterModal, SuccessModal } from '@/components/molecules/common/Modal';
import { DatePicker, Input } from '@/components/molecules/common/Form';
import styles from './Modal.module.scss';
import dayjs from 'dayjs';
import { Skeleton } from '@mui/material';

const View: React.FC<{ modal: ItemInterceptionModal }> = ({ modal }) => {
  const { t } = useTranslation();
  const { item, onClose } = modal;
  const commentMaxLength = 1000;

  const currentTime = dayjs(new Date()).add(1, 'day');
  const [dueDate, setDueDate] = useState<dayjs.Dayjs | null>(currentTime);
  const [comment, setComment] = useState('');
  const itemR = useItem(item.productId, false);

  const submitR = useUpdateItemInterception(item.productId);

  useEffect(() => {
    if (submitR.kind === 'Completed') {
      itemR.refetch();
    }
  }, [submitR.kind]);

  if (submitR.kind === 'Loading') {
    return (
      <NoFooterModal
        title={t('modal.item_interception.title')}
        body={
          <>
            <Skeleton
              height={20}
              variant="rounded"
            />
            <div className={styles.updateItemAttributeWait}>{t('modal.item_interception.waiting')}</div>
          </>
        }
        onClose={onClose}
        modalName="item_interception"
      />
    );
  }

  if (submitR.kind === 'Error') {
    return (
      <ErrorModal
        title={t('modal.item_interception.title')}
        error={submitR.error}
        onClose={onClose}
      />
    );
  }

  if (submitR.kind === 'Completed') {
    return (
      <SuccessModal
        title={t('modal.item_interception.title')}
        onClose={onClose}
      />
    );
  }

  const body = (
    <div className={styles.containerGap}>
      <div className={styles.text1rem}>{`${t('common.item')} #${item.productId}`}</div>
      <div className={styles.text1rem}>{t('modal.item_interception.description')}</div>
      <DatePicker
        required={true}
        disabled={false}
        label={t('modal.item_interception.date')}
        min={currentTime}
        onChange={setDueDate}
        value={dueDate || undefined}
      />
      <Input
        required={true}
        disabled={false}
        label={t('modal.item_interception.comment')}
        value={comment}
        multiline={true}
        rows={4}
        onChange={setComment}
        maxLength={commentMaxLength}
      />
    </div>
  );

  const onSubmit = () => {
    submitR.mutate({
      orderId: Number(item.orderId),
      date: dueDate != null ? dueDate.format('YYYY-MM-DD 00:00:00').toString() : '',
      comment: comment,
    });
  };

  return (
    <StandardModal
      title={t('modal.item_interception.title')}
      onClose={onClose}
      onSubmit={onSubmit}
      submittable={dueDate != null && comment != '' && item.orderId != ''}
      body={body}
      containerStyle={{ maxWidth: 650 }}
      modalName="item_interception"
    />
  );
};

export default View;
